import { useEffect, useState } from "react";

/**
 * Zegar odliczający do daty startu sprzedaży.
 * - Polska deklinacja (1 dzień / 2-4 dni / 5+ dni; godzina; minuta; sekunda)
 * - Renderuje 4 ciemne kafle: dni | godz. | min. | sek.
 * - Po osiągnięciu 0 pokazuje "Sprzedaż wystartowała!"
 */
function pluralPl(n, one, few, many) {
  const abs = Math.abs(n);
  const mod10 = abs % 10;
  const mod100 = abs % 100;
  if (abs === 1) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}

function pad(n) {
  return String(Math.max(0, n)).padStart(2, "0");
}

function calcRemaining(target) {
  if (!target) return { total: 0, days: 0, hours: 0, mins: 0, secs: 0 };
  const targetMs = new Date(target).getTime();
  const now = Date.now();
  const total = Math.max(0, targetMs - now);
  const days = Math.floor(total / (1000 * 60 * 60 * 24));
  const hours = Math.floor((total / (1000 * 60 * 60)) % 24);
  const mins = Math.floor((total / (1000 * 60)) % 60);
  const secs = Math.floor((total / 1000) % 60);
  return { total, days, hours, mins, secs };
}

export default function Countdown({ label = "Start za", targetAt, enabled = true }) {
  const [t, setT] = useState(() => calcRemaining(targetAt));

  useEffect(() => {
    if (!enabled || !targetAt) return;
    const id = setInterval(() => setT(calcRemaining(targetAt)), 1000);
    return () => clearInterval(id);
  }, [targetAt, enabled]);

  if (!enabled || !targetAt) return null;

  const daysLabel = pluralPl(t.days, "dzień", "dni", "dni");

  const cell = "flex-1 min-w-[68px] sm:min-w-[80px] rounded-xl bg-slate-900/85 border border-white/10 backdrop-blur-sm shadow-2xl px-3 py-3 sm:py-4 text-center";
  const num = "font-display font-black text-white text-3xl sm:text-4xl md:text-5xl leading-none tracking-tight";
  const cap = "text-white/60 text-[10px] sm:text-xs uppercase tracking-wider mt-1.5";

  if (t.total <= 0) {
    return (
      <div data-testid="countdown" className="flex justify-center py-4">
        <div className="inline-block rounded-full bg-[#dc2626] text-white font-bold px-6 py-2.5 shadow-lg shadow-[#dc2626]/40">
          Sprzedaż wystartowała!
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="countdown"
      className="relative z-50 flex flex-col items-center gap-3 py-4"
    >
      {label && (
        <div
          data-testid="countdown-label"
          className="text-white/80 text-sm sm:text-base font-semibold uppercase tracking-[0.15em]"
        >
          {label}
        </div>
      )}
      <div className="flex items-stretch gap-2 sm:gap-3 max-w-full">
        <div className={cell} data-testid="countdown-days">
          <div className={num}>{pad(t.days)}</div>
          <div className={cap}>{daysLabel}</div>
        </div>
        <div className={cell} data-testid="countdown-hours">
          <div className={num}>{pad(t.hours)}</div>
          <div className={cap}>godz.</div>
        </div>
        <div className={cell} data-testid="countdown-mins">
          <div className={num}>{pad(t.mins)}</div>
          <div className={cap}>min.</div>
        </div>
        <div className={cell} data-testid="countdown-secs">
          <div className={num}>{pad(t.secs)}</div>
          <div className={cap}>sek.</div>
        </div>
      </div>
    </div>
  );
}
